import { RecurrencePattern, TaskInstance } from "@/lib/api/checklist";
import moment from "moment-timezone";

export interface Period {
  label: string;
  start: Date;
  end: Date;
  key: string;
}

export function getPeriodsForRecurrence(
  pattern: RecurrencePattern,
  startDate: Date,
  endDate: Date
): Period[] {
  const periods: Period[] = [];
  const start = moment(startDate);
  const end = moment(endDate);

  switch (pattern) {
    case RecurrencePattern.ANNUALLY: {
      const startYear = start.year();
      const endYear = end.year();
      
      for (let year = startYear; year <= endYear; year++) {
        const yearStart = moment().year(year).startOf('year');
        const yearEnd = moment().year(year).endOf('year');
        
        // Only include if the period overlaps with our date range
        if (yearEnd.isSameOrAfter(start) && yearStart.isSameOrBefore(end)) {
          periods.push({
            label: year.toString(),
            start: yearStart.toDate(),
            end: yearEnd.toDate(),
            key: `year-${year}`,
          });
        }
      }
      break;
    }

    case RecurrencePattern.BIANNUALLY: {
      let current = moment(start).startOf('month');
      
      while (current.isSameOrBefore(end)) {
        const month = current.month();
        const year = current.year();
        
        if (month < 6) {
          // H1: Jan-Jun
          const h1Start = moment().year(year).month(0).startOf('month');
          const h1End = moment().year(year).month(5).endOf('month');
          
          if (h1End.isSameOrAfter(start) && h1Start.isSameOrBefore(end) && 
              !periods.some(p => p.key === `${year}-H1`)) {
            periods.push({
              label: `H1`,
              start: h1Start.toDate(),
              end: h1End.toDate(),
              key: `${year}-H1`,
            });
          }
        } else {
          // H2: Jul-Dec
          const h2Start = moment().year(year).month(6).startOf('month');
          const h2End = moment().year(year).month(11).endOf('month');
          
          if (h2End.isSameOrAfter(start) && h2Start.isSameOrBefore(end) && 
              !periods.some(p => p.key === `${year}-H2`)) {
            periods.push({
              label: `H2`,
              start: h2Start.toDate(),
              end: h2End.toDate(),
              key: `${year}-H2`,
            });
          }
        }
        
        current.add(6, 'months');
      }
      break;
    }

    case RecurrencePattern.QUARTERLY: {
      let current = moment(start).startOf('quarter');
      
      while (current.isSameOrBefore(end)) {
        const quarterStart = current.clone().startOf('quarter');
        const quarterEnd = current.clone().endOf('quarter');
        
        // Only include quarters that overlap with our date range
        if (quarterEnd.isSameOrAfter(start) && quarterStart.isSameOrBefore(end)) {
          const quarter = current.quarter();
          const year = current.year();
          
          periods.push({
            label: `Q${quarter}`,
            start: quarterStart.toDate(),
            end: quarterEnd.toDate(),
            key: `${year}-Q${quarter}`,
          });
        }
        
        current.add(1, 'quarter');
      }
      break;
    }

    case RecurrencePattern.MONTHLY: {
      let current = moment(start).startOf('month');
      
      while (current.isSameOrBefore(end)) {
        const monthStart = current.clone().startOf('month');
        const monthEnd = current.clone().endOf('month');
        
        // Only include months that overlap with our date range
        if (monthEnd.isSameOrAfter(start) && monthStart.isSameOrBefore(end)) {
          periods.push({
            label: current.format('MMM'),
            start: monthStart.toDate(),
            end: monthEnd.toDate(),
            key: `${current.year()}-${current.format('MM')}`,
          });
        }
        
        current.add(1, 'month');
      }
      break;
    }

    case RecurrencePattern.WEEKLY: {
      let current = moment(start).startOf('week');
      
      while (current.isSameOrBefore(end)) {
        const weekStart = current.clone().startOf('week');
        const weekEnd = current.clone().endOf('week');
        
        // Only include weeks that overlap with our date range
        if (weekEnd.isSameOrAfter(start) && weekStart.isSameOrBefore(end)) {
          periods.push({
            label: `W${current.week()}`,
            start: weekStart.toDate(),
            end: weekEnd.toDate(),
            key: `${current.year()}-W${current.format('WW')}`,
          });
        }
        
        current.add(1, 'week');
      }
      break;
    }
  }

  return periods;
}

export function getPeriodFromDate(date: Date, pattern: RecurrencePattern): string {
  const m = moment(date);
  const year = m.year();
  
  switch (pattern) {
    case RecurrencePattern.ANNUALLY:
      return `year-${year}`;
      
    case RecurrencePattern.BIANNUALLY:
      const half = m.month() < 6 ? "H1" : "H2";
      return `${year}-${half}`;
      
    case RecurrencePattern.QUARTERLY:
      const quarter = m.quarter();
      return `${year}-Q${quarter}`;
      
    case RecurrencePattern.MONTHLY:
      return `${year}-${m.format('MM')}`;
      
    case RecurrencePattern.WEEKLY:
      return `${year}-W${m.format('WW')}`;
      
    default:
      return "";
  }
}

export function groupInstancesByPeriod(
  instances: TaskInstance[],
  periods: Period[]
): Map<string, TaskInstance[]> {
  const grouped = new Map<string, TaskInstance[]>();
  
  // Initialize all periods with empty arrays
  periods.forEach(period => {
    grouped.set(period.key, []);
  });
  
  // Group instances by their due date period
  instances.forEach(instance => {
    const dueDate = new Date(instance.due_date);
    
    const period = periods.find(p => 
      dueDate >= p.start && dueDate <= p.end
    );
    
    if (period) {
      const existing = grouped.get(period.key) || [];
      grouped.set(period.key, [...existing, instance]);
    }
  });
  
  return grouped;
}

export function formatPeriodLabel(period: Period, pattern: RecurrencePattern): string {
  return period.label;
}

export function getDateRangeForRecurrence(pattern: RecurrencePattern, lookback: number): { start: Date; end: Date } {
  const now = moment();
  let start: moment.Moment;
  
  switch (pattern) {
    case RecurrencePattern.ANNUALLY:
      start = now.clone().subtract(lookback, 'years').startOf('year');
      break;
      
    case RecurrencePattern.BIANNUALLY:
      // For bi-annual, lookback is in number of half-years
      start = now.clone().subtract(lookback * 6, 'months').startOf('month');
      // Align to half-year boundary
      if (start.month() >= 6) {
        start.month(6).startOf('month');
      } else {
        start.month(0).startOf('month');
      }
      break;
      
    case RecurrencePattern.QUARTERLY:
      // Go back N quarters and start from the beginning of that quarter
      start = now.clone().subtract(lookback, 'quarters').startOf('quarter');
      break;
      
    case RecurrencePattern.MONTHLY:
      start = now.clone().subtract(lookback, 'months').startOf('month');
      break;
      
    case RecurrencePattern.WEEKLY:
      start = now.clone().subtract(lookback, 'weeks').startOf('week');
      break;
      
    default:
      start = now.clone().subtract(1, 'year').startOf('year');
  }
  
  // End date is the end of the current period
  let end: moment.Moment;
  switch (pattern) {
    case RecurrencePattern.ANNUALLY:
      end = now.clone().endOf('year');
      break;
    case RecurrencePattern.BIANNUALLY:
      end = now.clone().month() < 6 
        ? now.clone().month(5).endOf('month')
        : now.clone().month(11).endOf('month');
      break;
    case RecurrencePattern.QUARTERLY:
      end = now.clone().endOf('quarter');
      break;
    case RecurrencePattern.MONTHLY:
      end = now.clone().endOf('month');
      break;
    case RecurrencePattern.WEEKLY:
      end = now.clone().endOf('week');
      break;
    default:
      end = now.clone();
  }
  
  return { start: start.toDate(), end: end.toDate() };
}
